import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTracking } from '../hooks/useTracking';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import StripeExpressCheckoutBlock from '../components/StripeExpressCheckoutBlock';
import SingleStripePayment from '../components/SingleStripePayment';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { RecommendedProducts } from '../components/RecommendedProducts';
import { useCraftersCanSellBatch } from '../hooks/useCrafterCanSell';

/**
 * Determina il tipo di fornitura dell'ordine (usato dal modulo Assistenza ordine):
 * PERSONALIZED se nasce da una richiesta/proposta o se il cliente ha fornito
 * indicazioni personalizzate (testo, link, allegati). Altrimenti STANDARD.
 */
const getFulfillmentType = (items, isCustomOrder) => {
  if (isCustomOrder) return 'PERSONALIZED';
  const hasCustomInput = (items || []).some((item) => {
    if (item.isCustomOrder) return true;
    if (item.customerAttachments && Object.keys(item.customerAttachments).length > 0) return true;
    const values = item.customizationValues;
    return !!values && Object.values(values).some((v) => v && String(v).trim().length > 0);
  });
  return hasCustomInput ? 'PERSONALIZED' : 'STANDARD';
};
import { 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus, 
  CreditCard, 
  Truck, 
  MapPin,
  Shield,
  ChevronRight,
  Package,
  Check,
  AlertCircle,
  Lock,
  ArrowLeft,
  Loader2,
  Sparkles,
  Clock,
  Tag,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ShopCheckoutBanner from '../shop/ShopCheckoutBanner';
import { useShop } from '../shop/ShopContext';

export default function CartPage() {
  // Dentro il negozio personale: stessa logica, grafica e rimandi della bottega
  const shop = useShop();
  const { user, profile } = useAuth();
  const { trackPurchase } = useTracking();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cartItems, setCartItems] = useState([]);
  const [step, setStep] = useState(1); // 1: Cart, 2: Shipping, 3: Payment, 4: Confirmation
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [shippingMethod, setShippingMethod] = useState('standard');
  
  // Shipping form state
  const [shippingAddress, setShippingAddress] = useState({
    fullName: profile?.full_name || '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    phone: ''
  });

  // Pre-popola il nome dal profilo quando diventa disponibile (senza sovrascrivere modifiche utente)
  useEffect(() => {
    if (profile?.full_name) {
      setShippingAddress((prev) => (prev.fullName ? prev : { ...prev, fullName: profile.full_name }));
    }
  }, [profile?.full_name]);
  
  // Payment form state (for display only, Stripe handles actual payment)
  const [paymentMethod, setPaymentMethod] = useState('card');
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null); // { valid, discount_percentage, product_id, product_title, message }
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Dynamic shipping state (per crafter)
  const [shippingBreakdown, setShippingBreakdown] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  
  // Withdrawal rights acceptance for customized products
  const [withdrawalAccepted, setWithdrawalAccepted] = useState(false);
  // Milestone 2 - Express Checkout state
  const [checkoutMode, setCheckoutMode] = useState('redirect'); // 'redirect' | 'express'
  const [expressIntents, setExpressIntents] = useState([]);
  const [expressLoading, setExpressLoading] = useState(false);
  const [stripePromiseState, setStripePromiseState] = useState(null);
  
  // Track if we're already processing a payment (useRef to persist across renders)
  const isProcessingRef = useRef(false);

  // Verifica onboarding Stripe di tutti i crafter presenti nel carrello
  const cartCrafterIds = React.useMemo(
    () => Array.from(new Set(cartItems.map(i => i.crafterId || i.crafter?.id).filter(Boolean))),
    [cartItems]
  );
  const { results: crafterCanSellResults } = useCraftersCanSellBatch(cartCrafterIds);
  const blockedCrafterIds = React.useMemo(
    () => cartCrafterIds.filter(id => crafterCanSellResults[id] === false),
    [cartCrafterIds, crafterCanSellResults]
  );
  const blockedItems = React.useMemo(
    () => cartItems.filter(i => blockedCrafterIds.includes(i.crafterId || i.crafter?.id)),
    [cartItems, blockedCrafterIds]
  );
  const hasBlockedItems = blockedItems.length > 0;
  
  // API URL
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Load cart from localStorage
  useEffect(() => {
    const loadCart = () => {
      const cart = JSON.parse(localStorage.getItem('rarifex_cart') || '[]');
      setCartItems(cart);
    };
    
    loadCart();
    window.addEventListener('storage', loadCart);
    return () => window.removeEventListener('storage', loadCart);
  }, []);

  // Riallinea i prezzi salvati nel browser con quelli correnti (stesso calcolo del pagamento).
  // Una sola volta per apertura del carrello, quando il carrello e l'utente sono noti.
  const priceSyncDoneRef = useRef(false);
  useEffect(() => {
    if (priceSyncDoneRef.current || cartItems.length === 0) return;
    priceSyncDoneRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/checkout/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            buyer_id: user?.id || null,
            items: cartItems.map((it) => ({
              product_id: it.productId || it.id,
              crafter_id: it.crafterId || it.crafter?.id,
              price: Number(it.totalPrice ?? 0),
              quantity: Number(it.quantity || 1),
              title: it.title,
              selected_variants: it.selectedVariants || {},
              selected_customizations: it.selectedCustomizations || {},
              is_custom_order: !!it.isCustomOrder,
              request_id: it.requestId || null,
              proposal_id: it.proposalId || null,
            })),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 409 && typeof data?.detail === 'string') toast.error(data.detail);
          return;
        }
        const current = JSON.parse(localStorage.getItem('rarifex_cart') || '[]');
        if (current.length !== cartItems.length) return; // carrello cambiato nel frattempo
        let changed = false;
        const updated = current.map((it, idx) => {
          const q = (data.items || []).find((x) => x.index === idx);
          if (!q || Math.abs(Number(q.unit_price) - Number(it.totalPrice)) < 0.005) return it;
          changed = true;
          return { ...it, totalPrice: Number(q.unit_price) };
        });
        if (changed) {
          setCartItems(updated);
          localStorage.setItem('rarifex_cart', JSON.stringify(updated));
          window.dispatchEvent(new Event('cartUpdated'));
          toast.info('Alcuni prezzi del carrello sono stati aggiornati dagli artigiani.');
        }
      } catch (e) {
        console.error('price sync failed', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.length, user?.id]);

  // Milestone 2 - Load checkoutMode + Stripe publishable key
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    // Load checkout mode (redirect vs express)
    fetch(`${API_URL}/api/settings/checkout-mode`)
      .then((r) => r.json())
      .then((d) => setCheckoutMode(d.checkout_mode === 'express' ? 'express' : 'redirect'))
      .catch(() => setCheckoutMode('redirect'));
    // Reagisce a cambio da SuperAdmin
    const onModeChange = (e) => setCheckoutMode(e?.detail?.mode || 'redirect');
    window.addEventListener('checkoutModeChanged', onModeChange);
    return () => window.removeEventListener('checkoutModeChanged', onModeChange);
  }, []);

  // Lazy load Stripe.js quando serve
  useEffect(() => {
    if (checkoutMode !== 'express' || stripePromiseState) return;
    let cancelled = false;
    (async () => {
      let pubKey = '';
      // 1) Prova a leggere dal backend (rispetta is_live_mode del SuperAdmin)
      try {
        const res = await fetch(`${API_URL}/api/stripe/public-config`);
        if (res.ok) {
          const data = await res.json();
          pubKey = data?.publishable_key || '';
        }
      } catch (_) { /* fallback env */ }
      // 2) Fallback env locale
      if (!pubKey) pubKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
      if (!pubKey) {
        console.warn('Stripe publishable key non configurata (né su SuperAdmin né in env)');
        return;
      }
      const { loadStripe } = await import('@stripe/stripe-js');
      if (!cancelled) setStripePromiseState(loadStripe(pubKey));
    })();
    return () => { cancelled = true; };
  }, [checkoutMode, stripePromiseState]);

  
  // Load shipping costs when cart changes
  useEffect(() => {
    const loadShippingCosts = async () => {
      if (cartItems.length === 0) {
        setShippingBreakdown(null);
        return;
      }
      
      // Group items by crafter and calculate subtotals + item counts
      // NOTA: gli ordini personalizzati hanno una spedizione già concordata nella proposta
      // dell'artigiano (item.shippingCost) e NON devono passare dal calcolo standard.
      const regularItems = cartItems.filter(item => !item.isCustomOrder);
      if (regularItems.length === 0) {
        setShippingBreakdown(null);
        setLoadingShipping(false);
        return;
      }

      const crafterSubtotals = {};
      const crafterItemCounts = {};
      regularItems.forEach(item => {
        // Support both crafterId and crafter.id for backwards compatibility
        const crafterId = item.crafterId || item.crafter?.id;
        if (!crafterId) return;
        
        if (!crafterSubtotals[crafterId]) {
          crafterSubtotals[crafterId] = 0;
          crafterItemCounts[crafterId] = 0;
        }
        crafterSubtotals[crafterId] += item.totalPrice * item.quantity;
        // Count total items (considering quantity) for this crafter
        crafterItemCounts[crafterId] += item.quantity;
      });
      
      const crafterIds = Object.keys(crafterSubtotals);
      const subtotals = crafterIds.map(id => crafterSubtotals[id]);
      const itemCounts = crafterIds.map(id => crafterItemCounts[id]);
      
      if (crafterIds.length === 0) return;
      
      setLoadingShipping(true);
      try {
        const response = await fetch(
          `${API_URL}/api/shipping/calculate?crafter_ids=${crafterIds.join(',')}&subtotals=${subtotals.join(',')}&item_counts=${itemCounts.join(',')}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setShippingBreakdown(data);
        } else {
          // Fallback to default shipping
          setShippingBreakdown({
            breakdown: crafterIds.map((id, i) => ({
              crafter_id: id,
              subtotal: subtotals[i],
              item_count: itemCounts[i],
              shipping_cost: 5.00,
              rule_name: 'Spedizione Standard',
              delivery_days: '1-3 giorni lavorativi',
              free_shipping_applied: false,
              combine_shipping: true
            })),
            total_shipping: crafterIds.length * 5.00
          });
        }
      } catch (error) {
        console.error('Error loading shipping costs:', error);
        // Fallback
        setShippingBreakdown({
          breakdown: crafterIds.map((id, i) => ({
            crafter_id: id,
            subtotal: subtotals[i],
            item_count: itemCounts[i],
            shipping_cost: 5.00,
            rule_name: 'Spedizione Standard',
            delivery_days: '1-3 giorni lavorativi',
            free_shipping_applied: false,
            combine_shipping: true
          })),
          total_shipping: crafterIds.length * 5.00
        });
      } finally {
        setLoadingShipping(false);
      }
    };
    
    loadShippingCosts();
  }, [cartItems, API_URL]);
  
  // Check for payment return from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    // Check if this session was already processed (persisted in sessionStorage)
    const processedSessions = JSON.parse(sessionStorage.getItem('rarifex_processed_sessions') || '[]');
    const alreadyProcessed = processedSessions.includes(sessionId);
    
    // Prevent duplicate processing using ref and sessionStorage
    if (paymentStatus === 'success' && sessionId && !isProcessingRef.current && !alreadyProcessed) {
      isProcessingRef.current = true;
      
      // Mark this session as being processed
      sessionStorage.setItem('rarifex_processed_sessions', JSON.stringify([...processedSessions, sessionId]));
      
      // Verify payment and complete order
      verifyPaymentAndCompleteOrder(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Pagamento annullato');
      // Clear URL params
      navigate('/cart', { replace: true });
    } else if (alreadyProcessed && paymentStatus === 'success') {
      // Session already processed, redirect to dashboard
      console.log('Session already processed:', sessionId);
      navigate('/client/dashboard?tab=ordini', { replace: true });
    }
  }, [searchParams]);
  
  // Verify payment status and complete order
  const verifyPaymentAndCompleteOrder = async (sessionId) => {
    setCheckingPayment(true);
    setStep(3); // Show payment step while checking
    
    try {
      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 2000;
      
      const pollStatus = async () => {
        attempts++;
        
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }
        
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          // Payment successful - create order in Supabase
          await createOrderFromPayment(data.transaction, sessionId);
          return true;
        } else if (data.status === 'expired' || attempts >= maxAttempts) {
          toast.error('Sessione di pagamento scaduta. Riprova.');
          navigate('/cart', { replace: true });
          return false;
        }
        
        // Continue polling
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        return pollStatus();
      };
      
      await pollStatus();
      
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Errore nella verifica del pagamento');
      navigate('/cart', { replace: true });
    } finally {
      setCheckingPayment(false);
    }
  };
  
  // Lock to prevent concurrent order creation
  const orderCreationLockRef = useRef(false);
  
  // Create order from successful payment
  const createOrderFromPayment = async (orderData, sessionId) => {
    // Prevent concurrent calls
    if (orderCreationLockRef.current) {
      console.log('Order creation already in progress, skipping');
      return;
    }
    orderCreationLockRef.current = true;
    
    try {
      // First check if order already exists for this session
      const { data: existingOrders, error: checkError } = await supabase
        .from('orders')
        .select('order_number')
        .eq('stripe_session_id', sessionId)
        .limit(1);
      
      if (existingOrders && existingOrders.length > 0) {
        // Order already exists, just show confirmation
        console.log('Order already exists for session:', sessionId);
        setOrderNumber(existingOrders[0].order_number);
        setStep(4);
        // Clear cart
        localStorage.removeItem('rarifex_cart');
        localStorage.removeItem('rarifex_pending_cart');
        localStorage.removeItem('rarifex_pending_shipping');
        localStorage.removeItem('rarifex_pending_shipping_breakdown');
        window.dispatchEvent(new Event('cartUpdated'));
        navigate('/cart', { replace: true });
        return;
      }
      
      // Use orderData from backend (stored in MongoDB during checkout)
      // Fall back to localStorage if not available (for backwards compatibility)
      let savedCart = [];
      let savedShipping = {};
      
      if (orderData && orderData.items && orderData.items.length > 0) {
        // Use data from backend (preferred)
        savedCart = orderData.items;
        savedShipping = {
          address: orderData.shipping_address,
          method: orderData.shipping_method,
          cost: orderData.shipping_cost
        };
        console.log('Using order data from backend:', orderData);
      } else {
        // Fall back to localStorage
        savedCart = JSON.parse(localStorage.getItem('rarifex_pending_cart') || '[]');
        savedShipping = JSON.parse(localStorage.getItem('rarifex_pending_shipping') || '{}');
        console.log('Using order data from localStorage');
      }
      
      if (!savedCart.length) {
        // If no pending cart, order was already created
        const mockOrderNumber = 'RFX-' + sessionId.slice(-8).toUpperCase();
        setOrderNumber(mockOrderNumber);
        setStep(4);
        return;
      }
      
      // Group cart items by crafter
      const itemsByCrafter = savedCart.reduce((acc, item) => {
        const crafterId = item.crafterId || item.crafter?.id || 'unknown';
        if (!acc[crafterId]) {
          acc[crafterId] = {
            crafterId: crafterId,
            crafterName: item.crafterName || item.crafter?.name,
            crafter: item.crafter,
            items: []
          };
        }
        acc[crafterId].items.push(item);
        return acc;
      }, {});
      
      // Get shipping breakdown from localStorage or calculate default
      let crafterShippingMap = {};
      const savedShippingBreakdown = JSON.parse(localStorage.getItem('rarifex_pending_shipping_breakdown') || 'null');
      
      if (savedShippingBreakdown && savedShippingBreakdown.breakdown) {
        // Use saved breakdown
        savedShippingBreakdown.breakdown.forEach(s => {
          crafterShippingMap[s.crafter_id] = s.shipping_cost;
        });
      } else {
        // Default: €5 per crafter
        Object.keys(itemsByCrafter).forEach(crafterId => {
          crafterShippingMap[crafterId] = 5.00;
        });
      }
      
      // Create one order per crafter
      const orderPromises = Object.entries(itemsByCrafter).map(async ([crafterId, crafterData]) => {
        const crafterItems = crafterData.items;
        const crafterSubtotal = crafterItems.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
        // Use the specific shipping cost for this crafter (0 è un valore valido: spedizione gratuita)
        const crafterShipping = crafterShippingMap[crafterId] ?? 5.00;
        const crafterTotal = crafterSubtotal + crafterShipping;
        
        // Check if this is a custom order (from a request/proposal)
        const customOrderItem = crafterItems.find(item => item.isCustomOrder);
        const isCustomOrder = !!customOrderItem;
        
        const orderPayload = {
          client_id: user?.id || orderData?.client_id,
          crafter_id: crafterId !== 'unknown' ? crafterId : null,
          order_type: isCustomOrder ? 'request' : 'cart',
          order_source: isCustomOrder ? 'custom_request' : 'catalog',
          fulfillment_type: getFulfillmentType(crafterItems, isCustomOrder),
          title: crafterItems.length === 1 
            ? crafterItems[0].title 
            : `Ordine con ${crafterItems.length} prodotti da ${crafterData.crafter?.name || 'Artigiano'}`,
          description: isCustomOrder ? customOrderItem.customizationValues?.description : null,
          request_id: customOrderItem?.requestId || null,
          proposal_id: customOrderItem?.proposalId || null,
          agreed_price: isCustomOrder ? crafterSubtotal : null,
          agreed_timeline: customOrderItem?.customizationValues?.timeline || null,
          items: crafterItems.map(item => ({
            productId: item.productId,
            title: item.title,
            image: item.image,
            basePrice: item.basePrice,
            totalPrice: item.totalPrice,
            quantity: item.quantity,
            selectedVariants: item.selectedVariants,
            selectedCustomizations: item.selectedCustomizations,
            customizationValues: item.customizationValues,
            customerAttachments: item.customerAttachments || {},
            crafter: item.crafter,
            isCustomOrder: item.isCustomOrder || false
          })),
          shipping_address: savedShipping.address || {},
          shipping_method: savedShipping.method || 'standard',
          shipping_cost: crafterShipping,
          subtotal: crafterSubtotal,
          total: crafterTotal,
          payment_method: 'stripe',
          payment_status: 'paid',
          stripe_session_id: sessionId,
          status: 'confirmed',
          confirmed_at: new Date().toISOString()
        };

        console.log('Creating order for crafter:', crafterId, orderPayload);

        const { data: order, error } = await supabase
          .from('orders')
          .insert(orderPayload)
          .select('order_number, id')
          .single();

        if (error) {
          console.error('Order creation error for crafter:', crafterId, error);
          return null;
        }
        
        // If it's a custom order, update the direct_request with the order_id
        if (isCustomOrder && customOrderItem?.requestId && order?.id) {
          await supabase
            .from('direct_requests')
            .update({ order_id: order.id, status: 'in_progress' })
            .eq('id', customOrderItem.requestId);
        }
        
        console.log('Order created successfully:', order);
        return order;
      });

      const createdOrders = await Promise.all(orderPromises);
      const successfulOrders = createdOrders.filter(o => o !== null);
      
      // Decrement stock for purchased items
      if (successfulOrders.length > 0) {
        await decrementStockForItems(savedCart);
      }
      
      if (successfulOrders.length > 0) {
        // Show first order number (or combined if multiple)
        if (successfulOrders.length === 1) {
          setOrderNumber(successfulOrders[0].order_number);
        } else {
          setOrderNumber(`${successfulOrders.length} ordini creati`);
        }
        
        // Track purchase for recommendations
        const productIds = savedCart.map(item => item.productId).filter(Boolean);
        const crafterIds = savedCart.map(item => item.crafterId).filter(Boolean);
        const totalAmount = savedCart.reduce((sum, item) => sum + (item.finalPrice * item.quantity), 0);
        trackPurchase(productIds, crafterIds, totalAmount);
        
        // Mark discount code as used if one was applied
        const savedDiscount = JSON.parse(localStorage.getItem('rarifex_pending_discount') || 'null');
        if (savedDiscount && savedDiscount.code) {
          try {
            await fetch(`${API_URL}/api/discount/mark-used?code=${encodeURIComponent(savedDiscount.code)}&order_id=${successfulOrders[0].id}`, {
              method: 'POST'
            });
            localStorage.removeItem('rarifex_pending_discount');
          } catch (err) {
            console.error('Error marking discount as used:', err);
          }
        }
        
        // Send email notifications (async, don't block UI)
        sendOrderEmails(successfulOrders, savedCart, savedShipping, savedDiscount);
        
        // Send custom order notifications via messages (async, don't block UI)
        sendCustomOrderNotifications(savedCart, successfulOrders);
      } else {
        const mockOrderNumber = 'RFX-' + sessionId.slice(-8).toUpperCase();
        setOrderNumber(mockOrderNumber);
      }

      // Clear cart and pending data
      localStorage.removeItem('rarifex_cart');
      localStorage.removeItem('rarifex_pending_cart');
      localStorage.removeItem('rarifex_pending_shipping');
      localStorage.removeItem('rarifex_pending_shipping_breakdown');
      localStorage.removeItem('rarifex_pending_discount');
      localStorage.removeItem('rarifex_pending_client');
      window.dispatchEvent(new Event('cartUpdated'));
      
      toast.success(successfulOrders.length > 1 
        ? `${successfulOrders.length} ordini creati con successo!` 
        : 'Ordine completato con successo!');
      
      // Redirect to client dashboard orders tab
      navigate('/client/dashboard?tab=ordini', { replace: true });
      
    } catch (error) {
      console.error('Order creation error:', error);
      toast.error('Errore nella creazione dell\'ordine');
    } finally {
      orderCreationLockRef.current = false;
    }
  };

  // Send order notification emails
  const sendOrderEmails = async (orders, cartItems, shippingData, discountData) => {
    try {
      // Calculate totals
      const subtotal = cartItems.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
      const shippingCost = shippingData?.cost || 0;
      const discountAmount = discountData?.amount || 0;
      const total = subtotal + shippingCost - discountAmount;
      
      // Send confirmation to client
      if (user?.email && profile?.full_name) {
        const orderNumber = orders.length === 1 ? orders[0].order_number : `${orders.length} ordini`;
        
        fetch(`${API_URL}/api/email/order-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_email: user.email,
            client_name: profile.full_name || 'Cliente',
            order_number: orderNumber,
            items: cartItems.map(item => ({
              title: item.title,
              image: item.image,
              quantity: item.quantity,
              totalPrice: item.totalPrice,
              crafterName: item.crafterName || item.crafter?.name || null
            })),
            subtotal,
            shipping_cost: shippingCost,
            discount_amount: discountAmount,
            total,
            shipping_address: shippingData?.address || {}
          })
        }).catch(err => console.error('Error sending client email:', err));
      }
      
      // Send notification to each crafter
      const crafterGroups = {};
      cartItems.forEach(item => {
        const crafterId = item.crafter?.id;
        if (crafterId) {
          if (!crafterGroups[crafterId]) {
            crafterGroups[crafterId] = {
              crafter: item.crafter,
              items: []
            };
          }
          crafterGroups[crafterId].items.push(item);
        }
      });
      
      // Find the order for each crafter and send email
      for (const [crafterId, data] of Object.entries(crafterGroups)) {
        const crafterOrder = orders.find(o => o.crafter_id === crafterId);
        if (crafterOrder && data.crafter?.email) {
          const crafterTotal = data.items.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
          
          fetch(`${API_URL}/api/email/new-order-crafter`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              crafter_email: data.crafter.email,
              crafter_name: data.crafter.name || 'Artigiano',
              order_number: crafterOrder.order_number,
              client_name: profile?.full_name || 'Cliente',
              items: data.items.map(item => ({
                title: item.title,
                quantity: item.quantity,
                totalPrice: item.totalPrice
              })),
              total: crafterTotal
            })
          }).catch(err => console.error('Error sending crafter email:', err));
        }
      }
      
      console.log('Order emails queued for sending');
    } catch (error) {
      console.error('Error preparing order emails:', error);
    }
  };

  // Send custom order notifications via messages
  const sendCustomOrderNotifications = async (cartItems, orders) => {
    try {
      console.log('sendCustomOrderNotifications called with:', { cartItems, orders });
      
      // Get saved client info from localStorage (saved before Stripe redirect)
      const savedClient = JSON.parse(localStorage.getItem('rarifex_pending_client') || '{}');
      const clientId = savedClient.id || user?.id;
      const clientName = savedClient.name || profile?.full_name || 'Cliente';
      
      if (!clientId) {
        console.error('No client ID available for notifications');
        return;
      }
      
      // Find custom order items (from requests/proposals)
      const customItems = cartItems.filter(item => item.isCustomOrder && item.requestId && item.proposalId);
      console.log('Custom items found:', customItems);
      
      for (const item of customItems) {
        // Find the corresponding order
        const order = orders.find(o => o.order_number);
        if (!order) {
          console.log('No order found for item:', item);
          continue;
        }
        
        const notificationData = {
          request_id: item.requestId,
          client_id: clientId,
          client_name: clientName,
          selected_crafter_id: item.crafterId,
          order_number: order.order_number,
          request_title: item.title,
          frontend_url: window.location.origin
        };
        
        console.log('Sending custom order notification:', notificationData);
        
        // Call the backend notification endpoint
        const response = await fetch(`${API_URL}/api/notifications/custom-order-completed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(notificationData)
        });
        
        const result = await response.json();
        console.log('Notification response:', result);
      }
      
      // Clean up saved client info
      localStorage.removeItem('rarifex_pending_client');
      
      console.log('Custom order notifications completed');
    } catch (error) {
      console.error('Error preparing custom order notifications:', error);
    }
  };

  // Decrement stock for purchased items
  const decrementStockForItems = async (items) => {
    try {
      for (const item of items) {
        const productId = item.productId;
        const quantity = item.quantity || 1;

        // Get current product data (need also `variants` array to build the same
        // key used by ProductDetailPage: `type:value|type2:value2` without sort)
        const { data: product, error: fetchError } = await supabase
          .from('products')
          .select('customization_options')
          .eq('id', productId)
          .single();

        if (fetchError || !product) {
          console.warn(`Could not fetch product ${productId} for stock update:`, fetchError);
          continue;
        }

        const currentOptions = product.customization_options || {};
        const variantSettings = currentOptions.variantSettings || {};
        const inventory = currentOptions.inventory || {};
        const productVariants = currentOptions.variants || [];

        // Build variant key coherently with ProductDetailPage:
        //   variants.map(v => `${v.type}:${selected[v.type] || v.values[0]}`).join('|')
        let variantKey = 'default';
        if (productVariants.length > 0) {
          variantKey = productVariants.map((v) => {
            const chosen = item.selectedVariants?.[v.type] || v.values?.[0];
            return `${v.type}:${chosen}`;
          }).join('|');
        }

        // Riconosci se il prodotto ha tracking di stock:
        // - variantSettings[key] con timeline=immediate  OPPURE
        // - inventory[key] è un numero valorizzato (dato "legacy" ma attuale)
        const vs = variantSettings[variantKey];
        const legacyStock = inventory[variantKey];
        const hasVariantSettings = vs && typeof vs === 'object';
        const isMadeToOrder = hasVariantSettings
          ? vs.timeline && vs.timeline !== 'immediate'
          : (legacyStock === undefined || legacyStock === null);

        if (isMadeToOrder) {
          console.log(`Product ${productId} variant ${variantKey}: made-to-order (skip stock)`);
          continue;
        }

        // Fonte di verità dello stock: variantSettings[key].stock se presente,
        // altrimenti inventory[key] (dato attualmente in uso)
        const currentStock = hasVariantSettings
          ? (vs.stock ?? legacyStock ?? 0)
          : (legacyStock ?? 0);
        const newStock = Math.max(0, currentStock - quantity);

        const updatedVariantSettings = hasVariantSettings
          ? { ...variantSettings, [variantKey]: { ...vs, stock: newStock } }
          : variantSettings;
        const updatedInventory = { ...inventory, [variantKey]: newStock };

        const updatedOptions = {
          ...currentOptions,
          variantSettings: updatedVariantSettings,
          inventory: updatedInventory,
        };

        const { error: updateError } = await supabase
          .from('products')
          .update({ customization_options: updatedOptions })
          .eq('id', productId);

        if (updateError) {
          console.error(`Stock update FAILED for ${productId} [${variantKey}]:`, updateError);
        } else {
          console.log(`Stock decremented for ${productId} [${variantKey}]: ${currentStock} → ${newStock}`);
        }
      }
    } catch (error) {
      console.error('Error decrementing stock:', error);
      // Non-blocking: order still valid
    }
  };

  /**
   * Gestore risultati multi-charge (nuovo flusso SetupIntent + N PI).
   * Riceve { order_id, results: [{crafter_id, payment_intent_id, status, amount, items}] }
   * e per OGNI charge succeeded esegue la stessa pipeline post-payment del
   * flusso legacy: crea record `orders`, decrementa stock, notifiche email/chat.
   */
  const handleMultiChargeResults = async (chargeData) => {
    const successful = (chargeData?.results || []).filter(
      (r) => r.status === 'succeeded' && r.order_id
    );
    if (successful.length === 0) return;

    // Gli ordini sono già creati dal server (multi-charge), insieme a scalo stock e
    // notifiche email/chat. Qui restano solo gli effetti lato browser.
    const paidCrafterIds = new Set(successful.map((r) => r.crafter_id));
    const paidItems = cartItems.filter((it) => paidCrafterIds.has(it.crafterId || it.crafter?.id));

    try {
      sendCustomOrderNotifications(
        paidItems,
        successful.map((r) => ({ id: r.order_id, order_number: r.order_number }))
      );
    } catch (e) { console.error('sendCustomOrderNotifications failed', e); }

    for (const res of successful) {
      try {
        const crafterItems = paidItems.filter((it) => (it.crafterId || it.crafter?.id) === res.crafter_id);
        trackPurchase(crafterItems.map((i) => i.productId).filter(Boolean), [res.crafter_id], res.amount);
      } catch (_) { /* no-op */ }
    }

    // Rimuovi dal carrello solo gli articoli degli artigiani pagati
    const remaining = cartItems.filter((it) => !paidCrafterIds.has(it.crafterId || it.crafter?.id));
    setCartItems(remaining);
    localStorage.setItem('rarifex_cart', JSON.stringify(remaining));
    window.dispatchEvent(new Event('cartUpdated'));

    // Se tutti succeeded → naviga a pagina di successo (passo i numeri ordine)
    if (chargeData.all_succeeded) {
      const firstPi = chargeData.results[0]?.payment_intent_id;
      const orderNumbers = successful.map((r) => r.order_number).filter(Boolean);
      const ordersParam = orderNumbers.length
        ? `&orders=${encodeURIComponent(orderNumbers.join(','))}`
        : '';
      window.location.href =
        `/order-success?pi=${firstPi}&redirect_status=succeeded${ordersParam}`;
    }
    // Se parziale → lascia il componente mostrare i retry
  };

  // Update cart in localStorage
  const updateCart = (newCart) => {
    setCartItems(newCart);
    localStorage.setItem('rarifex_cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  // Remove item from cart
  const removeItem = (index) => {
    const newCart = cartItems.filter((_, i) => i !== index);
    updateCart(newCart);
    toast.success('Prodotto rimosso dal carrello');
  };

  // Update quantity
  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const newCart = [...cartItems];
    newCart[index].quantity = newQuantity;
    updateCart(newCart);
  };

  // Validate discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Inserisci un codice sconto');
      return;
    }
    
    if (!user) {
      toast.error('Accedi per utilizzare un codice sconto');
      return;
    }

    setValidatingDiscount(true);
    try {
      const productIds = cartItems.map(item => item.productId);
      
      const response = await fetch(`${API_URL}/api/discount/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: discountCode.trim().toUpperCase(),
          user_id: user.id,
          product_ids: productIds
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setDiscountApplied(data);
        toast.success(data.message || 'Codice sconto applicato!');
      } else {
        setDiscountApplied(null);
        toast.error(data.error || 'Codice sconto non valido');
      }
    } catch (error) {
      console.error('Error validating discount:', error);
      toast.error('Errore nella verifica del codice');
      setDiscountApplied(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  // Remove discount
  const removeDiscount = () => {
    setDiscountApplied(null);
    setDiscountCode('');
    toast.info('Sconto rimosso');
  };

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
  const getShippingCost = () => {
    // Calculate shipping for custom orders (from proposals)
    const customOrdersShipping = cartItems
      .filter(item => item.isCustomOrder && item.shippingCost !== undefined)
      .reduce((sum, item) => sum + (item.shippingCost || 0), 0);
    
    // Calculate shipping for regular products
    const regularItems = cartItems.filter(item => !item.isCustomOrder);
    let regularShipping = 0;
    
    if (regularItems.length > 0) {
      // Use dynamic shipping breakdown if available
      if (shippingBreakdown && shippingBreakdown.total_shipping !== undefined) {
        regularShipping = shippingBreakdown.total_shipping;
      } else {
        // Fallback to default calculation - count unique crafters for regular items
        const uniqueCrafters = new Set(regularItems.map(item => item.crafterId || item.crafter?.id).filter(Boolean));
        regularShipping = 5.00 * uniqueCrafters.size;
      }
    }
    
    return customOrdersShipping + regularShipping;
  };
  const shippingCost = getShippingCost();

  /**
   * Righe di spedizione mostrate al cliente: le spedizioni concordate negli ordini
   * personalizzati + quelle calcolate dalle regole degli artigiani per i prodotti a catalogo.
   * La somma di queste righe coincide sempre con `shippingCost` (l'importo effettivamente pagato).
   */
  const getShippingRows = () => {
    const customRows = [];
    const seen = new Set();
    cartItems
      .filter(item => item.isCustomOrder)
      .forEach(item => {
        const crafterId = item.crafterId || item.crafter?.id || 'custom';
        const cost = Number(item.shippingCost || 0);
        const existing = customRows.find(r => r.crafter_id === crafterId);
        if (existing) {
          existing.shipping_cost += cost;
          existing.free_shipping_applied = existing.shipping_cost === 0;
          return;
        }
        seen.add(crafterId);
        customRows.push({
          crafter_id: crafterId,
          shipping_cost: cost,
          rule_name: 'Spedizione concordata con l\'artigiano',
          delivery_days: item.customizationValues?.timeline || 'Come da proposta',
          free_shipping_applied: cost === 0,
          is_custom_order: true,
        });
      });

    const regularRows = Array.isArray(shippingBreakdown?.breakdown) ? shippingBreakdown.breakdown : [];
    return [...customRows, ...regularRows];
  };
  const shippingRows = getShippingRows();
  
  // Get breakdown of shipping (custom vs regular)
  const getShippingBreakdownInfo = () => {
    const customOrdersShipping = cartItems
      .filter(item => item.isCustomOrder && item.shippingCost !== undefined)
      .reduce((sum, item) => sum + (item.shippingCost || 0), 0);
    
    const regularItems = cartItems.filter(item => !item.isCustomOrder);
    let regularShipping = 0;
    
    if (regularItems.length > 0) {
      if (shippingBreakdown && shippingBreakdown.total_shipping !== undefined) {
        regularShipping = shippingBreakdown.total_shipping;
      } else {
        const uniqueCrafters = new Set(regularItems.map(item => item.crafterId || item.crafter?.id).filter(Boolean));
        regularShipping = 5.00 * uniqueCrafters.size;
      }
    }
    
    return { customOrdersShipping, regularShipping };
  };
  
  // Calculate discount amount
  const calculateDiscountAmount = () => {
    if (!discountApplied || !discountApplied.valid) return 0;
    
    const discountPercentage = discountApplied.discount_percentage;
    
    // If discount is for a specific product
    if (discountApplied.product_id) {
      const discountedItem = cartItems.find(item => item.productId === discountApplied.product_id);
      if (discountedItem) {
        return (discountedItem.totalPrice * discountedItem.quantity) * (discountPercentage / 100);
      }
      return 0;
    }
    
    // Otherwise apply to entire cart
    return subtotal * (discountPercentage / 100);
  };
  
  const discountAmount = calculateDiscountAmount();
  const total = subtotal + shippingCost - discountAmount;

  // Check if cart has any customized/custom products
  const hasCustomizedProducts = cartItems.some(item => 
    item.isCustomOrder || Object.keys(item.selectedCustomizations || {}).some(k => item.selectedCustomizations[k])
  );

  // Format variant display
  const formatVariants = (variants) => {
    if (!variants || Object.keys(variants).length === 0) return null;
    return Object.entries(variants).map(([key, value]) => `${key}: ${value}`).join(', ');
  };

  // Handle checkout
  const handleCheckout = () => {
    if (!user) {
      toast.error('Accedi per completare l\'acquisto');
      navigate('/login');
      return;
    }
    setStep(2);
  };

  // Handle shipping submit
  const handleShippingSubmit = (e) => {
    e.preventDefault();
    if (!shippingAddress.fullName || !shippingAddress.address || !shippingAddress.city || 
        !shippingAddress.postalCode || !shippingAddress.province) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    setStep(3);
  };

  // Milestone 2 - Crea multi-PaymentIntent per Express Checkout in-page
  const createExpressIntents = async () => {
    setExpressLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Ripartisci lo shipping cost tra gli items (una volta per crafter)
      // Include sia le spedizioni concordate (ordini personalizzati) sia quelle calcolate
      const shippingPerCrafter = {};
      for (const row of shippingRows) {
        if (row?.crafter_id) {
          shippingPerCrafter[row.crafter_id] = Number(row.shipping_cost || 0);
        }
      }
      const seenShipping = new Set();
      const payloadItems = cartItems.map((it) => {
        const cid = it.crafterId || it.crafter?.id;
        const includeShipping = cid && !seenShipping.has(cid);
        if (cid) seenShipping.add(cid);
        // Il prezzo unitario finale (con sconti/varianti/personalizzazioni)
        // nel carrello è in `totalPrice`. Fallback a `price` e `basePrice`.
        const unitPrice = Number(it.totalPrice ?? it.price ?? it.basePrice ?? 0);
        return {
          product_id: it.productId || it.id,
          crafter_id: cid,
          price: unitPrice,
          quantity: Number(it.quantity || 1),
          title: it.title || it.name,
          shipping_cost: includeShipping ? Number(shippingPerCrafter[cid] || 0) : 0,
        };
      });

      const resp = await fetch(`${API_URL}/api/checkout/create-multi-payment-intents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: payloadItems,
          buyer_id: user?.id || null,
          buyer_email: user?.email || shippingAddress.email || null,
          shipping_address: shippingAddress,
          currency: 'eur',
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.detail || 'Impossibile creare i pagamenti');
        setExpressLoading(false);
        return;
      }
      setExpressIntents(data.intents || []);
    } catch (err) {
      console.error('createExpressIntents error:', err);
      toast.error('Errore durante la creazione del pagamento');
    } finally {
      setExpressLoading(false);
    }
  };

  // Se il carrello ha UN SOLO artigiano, saltiamo la schermata intermedia
  // "Prepara i pagamenti" e prepariamo i PaymentIntent automaticamente non
  // appena Stripe è pronto sullo step di pagamento.
  // NOTA: rimpiazzato dal nuovo flow SingleStripePayment (SetupIntent + multi-charge).
  // Manteniamo l'utility ma disabilitiamo l'auto-trigger.
  // eslint-disable-next-line no-unused-vars
  const _unused_auto_trigger = null;

  // Handle payment submit - redirects to Stripe Checkout
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    // Validate withdrawal acceptance for customized products
    if (hasCustomizedProducts && !withdrawalAccepted) {
      toast.error('Devi accettare le condizioni per i prodotti personalizzati');
      return;
    }
    
    setLoading(true);
    
    try {
      // Save cart and shipping data for after payment
      localStorage.setItem('rarifex_pending_cart', JSON.stringify(cartItems));
      localStorage.setItem('rarifex_pending_shipping', JSON.stringify({
        address: shippingAddress,
        method: shippingMethod,
        cost: shippingCost
      }));
      // Save shipping breakdown per crafter (spedizioni concordate + calcolate)
      if (shippingRows.length > 0) {
        localStorage.setItem('rarifex_pending_shipping_breakdown', JSON.stringify({
          breakdown: shippingRows,
          total_shipping: shippingCost,
        }));
      }
      
      // Save client info for notifications after payment
      localStorage.setItem('rarifex_pending_client', JSON.stringify({
        id: user?.id,
        name: profile?.full_name || 'Cliente'
      }));
      
      // Save discount info if applied
      if (discountApplied && discountApplied.valid) {
        localStorage.setItem('rarifex_pending_discount', JSON.stringify({
          code: discountCode,
          percentage: discountApplied.discount_percentage,
          amount: discountAmount,
          product_id: discountApplied.product_id
        }));
      } else {
        localStorage.removeItem('rarifex_pending_discount');
      }
      
      // Prepare order data for backend
      const orderData = {
        client_id: user?.id,
        items: cartItems,
        shipping_address: shippingAddress,
        shipping_method: shippingMethod,
        shipping_cost: shippingCost,
        subtotal: subtotal,
        discount_code: discountApplied?.valid ? discountCode : null,
        discount_percentage: discountApplied?.valid ? discountApplied.discount_percentage : null,
        discount_amount: discountAmount,
        total: total
      };
      
      // Call backend to create Stripe Checkout session
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: total,
          origin_url: window.location.origin,
          order_data: orderData,
          user_id: user?.id || null,
          user_email: user?.email || profile?.email || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Errore durante l\'avvio del pagamento. Riprova.');
      setLoading(false);
    }
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[
        { num: 1, label: 'Carrello' },
        { num: 2, label: 'Spedizione' },
        { num: 3, label: 'Pagamento' },
        { num: 4, label: 'Conferma' }
      ].map((s, idx) => (
        <React.Fragment key={s.num}>
          <div className={`flex items-center gap-2 ${step >= s.num ? 'text-shop-primary' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step > s.num ? 'bg-shop-primary text-white' : 
              step === s.num ? 'bg-shop-primary text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {step > s.num ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
          </div>
          {idx < 3 && <ChevronRight className="w-4 h-4 text-slate-300" />}
        </React.Fragment>
      ))}
    </div>
  );

  // Empty cart
  if (cartItems.length === 0 && step === 1) {
    return (
      <div className={`min-h-screen ${shop ? 'pt-8' : 'bg-slate-50 pt-32'} pb-12`} data-testid="cart-page-empty">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2" data-testid="empty-cart-title">Il tuo carrello è vuoto</h2>
            <p className="text-slate-600 mb-6">{shop ? `Scopri le creazioni di ${shop.name}` : 'Scopri i prodotti artigianali Made in Italy'}</p>
            <Link to="/products">
              <Button className="bg-shop-primary text-white">
                <Package className="w-4 h-4 mr-2" />
                {shop ? 'Torna alle creazioni' : 'Esplora i prodotti'}
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  // Confirmation step
  if (step === 4) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 pb-12" data-testid="cart-page-confirmation">
        <div className="max-w-2xl mx-auto px-4">
          <StepIndicator />
          <Card className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2" data-testid="order-confirmed-title">Ordine Confermato!</h2>
            <p className="text-slate-600 mb-4">
              Grazie per il tuo acquisto. Riceverai una email di conferma a breve.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Numero ordine: <span className="font-mono font-bold" data-testid="order-number">{orderNumber || 'RFX-' + Date.now().toString(36).toUpperCase()}</span>
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 text-left">
                  <strong>Nota:</strong> Questo è un ordine di prova. In produzione riceveresti 
                  email di conferma e tracking della spedizione.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <Link to="/products">
                <Button variant="outline">Continua lo shopping</Button>
              </Link>
              <Link to="/dashboard?tab=cliente">
                <Button className="bg-shop-primary text-white">Vai ai miei ordini</Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${shop ? 'pt-8' : 'bg-slate-50 pt-32'} pb-40 md:pb-12`} data-testid="cart-page">
      <div className="max-w-6xl mx-auto px-4">
        <ShopCheckoutBanner />
        <StepIndicator />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Cart Items */}
            {step === 1 && (
              <Card className="p-6" data-testid="cart-items-section">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2" data-testid="cart-title">
                  <ShoppingCart className="w-5 h-5 text-shop-primary" />
                  Il tuo carrello ({cartItems.length} {cartItems.length === 1 ? 'articolo' : 'articoli'})
                </h2>
                
                <div className="space-y-4">
                  <AnimatePresence>
                    {cartItems.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        className={`flex gap-4 p-4 rounded-lg ${item.isCustomOrder ? 'bg-shop-accent/5 border border-shop-accent/20' : 'bg-slate-50'}`}
                        data-testid={`cart-item-${index}`}
                      >
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <div className="w-24 h-24 rounded-lg overflow-hidden bg-white relative">
                            {item.image ? (
                              <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-shop-accent/20 to-shop-primary/20">
                                {item.isCustomOrder ? (
                                  <Sparkles className="w-8 h-8 text-shop-accent" />
                                ) : (
                                  <Package className="w-8 h-8 text-slate-300" />
                                )}
                              </div>
                            )}
                            {item.isCustomOrder && (
                              <div className="absolute top-0 right-0 bg-shop-accent text-white text-[10px] font-bold px-1.5 py-0.5 rounded-bl">
                                SU MISURA
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-800 truncate">
                              {item.title}
                            </h3>
                            {item.isCustomOrder && (
                              <Badge className="bg-shop-accent/10 text-shop-accent border-shop-accent/30 text-xs">
                                Creazione Personalizzata
                              </Badge>
                            )}
                          </div>
                          
                          {item.crafter && (
                            <p className="text-sm text-slate-500">di {item.crafter.name}</p>
                          )}
                          
                          {/* Description for custom orders */}
                          {item.isCustomOrder && item.customizationValues?.description && (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2 italic">
                              "{item.customizationValues.description}"
                            </p>
                          )}
                          
                          {/* Timeline for custom orders */}
                          {item.isCustomOrder && item.customizationValues?.timeline && (
                            <p className="text-xs text-shop-primary mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Tempo stimato: {item.customizationValues.timeline === '1-2weeks' ? '1-2 settimane' : 
                                item.customizationValues.timeline === '1month' ? '1 mese' : 
                                item.customizationValues.timeline === 'urgent' ? 'Urgente' : item.customizationValues.timeline}
                            </p>
                          )}
                          
                          {/* Variants (for regular products) */}
                          {!item.isCustomOrder && formatVariants(item.selectedVariants) && (
                            <p className="text-xs text-slate-500 mt-1">
                              {formatVariants(item.selectedVariants)}
                            </p>
                          )}
                          
                          {/* Customizations (for regular products) */}
                          {!item.isCustomOrder && Object.keys(item.selectedCustomizations || {}).some(k => item.selectedCustomizations[k]) && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              Personalizzato
                            </Badge>
                          )}
                          
                          {/* Withdrawal Warning for customized/custom products */}
                          {(item.isCustomOrder || Object.keys(item.selectedCustomizations || {}).some(k => item.selectedCustomizations[k])) && (
                            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`withdrawal-warning-${index}`}>
                              <p className="text-xs text-amber-700 flex items-start gap-1.5">
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-600" />
                                <span><strong>Prodotto su misura:</strong> recesso escluso (art. 59 Cod. Consumo)</span>
                              </p>
                            </div>
                          )}
                          
                          {/* Price */}
                          <div className="mt-2">
                            <span className="font-bold text-shop-primary">€{item.totalPrice.toFixed(2)}</span>
                            {item.totalPrice !== item.basePrice && !item.isCustomOrder && (
                              <span className="text-xs text-slate-400 line-through ml-2">€{item.basePrice.toFixed(2)}</span>
                            )}
                            {/* Show shipping cost for custom orders */}
                            {item.isCustomOrder && item.shippingCost !== undefined && (
                              <span className={`text-xs ml-2 ${item.shippingCost === 0 ? 'text-green-600' : 'text-slate-500'}`}>
                                {item.shippingCost === 0 ? '+ spedizione gratuita' : `+ €${item.shippingCost.toFixed(2)} sped.`}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Quantity & Remove */}
                        <div className="flex flex-col items-end justify-between">
                          <button
                            onClick={() => removeItem(index)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          
                          {/* Quantity controls only for non-custom orders */}
                          {!item.isCustomOrder ? (
                            <div className="flex items-center gap-2 bg-white rounded-lg border p-1">
                              <button
                                onClick={() => updateQuantity(index, item.quantity - 1)}
                                className="p-1 hover:bg-slate-100 rounded"
                                disabled={item.quantity <= 1}
                                data-testid={`quantity-decrease-${index}`}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-8 text-center font-medium" data-testid={`quantity-value-${index}`}>{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(index, item.quantity + 1)}
                                className="p-1 hover:bg-slate-100 rounded"
                                data-testid={`quantity-increase-${index}`}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-xs text-slate-500">
                              Pezzo unico
                            </Badge>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </Card>
            )}

            {/* Recommendations Section - Only on Step 1 */}
            {step === 1 && cartItems.length > 0 && (
              <div className="mt-8" data-testid="cart-suggestions-wrapper">
                <RecommendedProducts 
                  context="cart"
                  limit={4}
                  variant="compact"
                  title="Ti potrebbe interessare anche"
                  subtitle="Suggerimenti dagli artigiani della piattaforma — clicca per esplorare"
                  excludeIds={cartItems.map(item => item.productId).filter(Boolean)}
                  className="bg-teal-50/40 rounded-xl p-5 border-2 border-dashed border-teal-200"
                />
              </div>
            )}

            {/* Step 2: Shipping */}
            {step === 2 && (
              <Card className="p-6" data-testid="shipping-section">
                <div className="flex items-center gap-2 mb-6">
                  <button onClick={() => setStep(1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-cart">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Truck className="w-5 h-5 text-shop-primary" />
                    Indirizzo di spedizione
                  </h2>
                </div>
                
                <form onSubmit={handleShippingSubmit} className="space-y-4">
                  <div>
                    <Label>Nome completo *</Label>
                    <Input
                      value={shippingAddress.fullName}
                      onChange={(e) => setShippingAddress({...shippingAddress, fullName: e.target.value})}
                      placeholder="Mario Rossi"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Indirizzo *</Label>
                    <Input
                      value={shippingAddress.address}
                      onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})}
                      placeholder="Via Roma 123"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Città *</Label>
                      <Input
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                        placeholder="Milano"
                        required
                      />
                    </div>
                    <div>
                      <Label>CAP *</Label>
                      <Input
                        value={shippingAddress.postalCode}
                        onChange={(e) => setShippingAddress({...shippingAddress, postalCode: e.target.value})}
                        placeholder="20100"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Provincia *</Label>
                      <Input
                        value={shippingAddress.province}
                        onChange={(e) => setShippingAddress({...shippingAddress, province: e.target.value})}
                        placeholder="MI"
                        required
                      />
                    </div>
                    <div>
                      <Label>Telefono</Label>
                      <Input
                        value={shippingAddress.phone}
                        onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>

                  {/* Shipping Options - Dynamic per Crafter */}
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Riepilogo Spedizioni
                    </h3>
                    
                    {loadingShipping ? (
                      <div className="flex items-center gap-2 py-4 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Calcolo spedizioni in corso...
                      </div>
                    ) : shippingRows.length > 0 ? (
                      <div className="space-y-3">
                        {shippingRows.map((shipping, idx) => {
                          // Find crafter name from cart items (support both formats)
                          const crafterItem = cartItems.find(item => 
                            (item.crafterId || item.crafter?.id) === shipping.crafter_id
                          );
                          const crafterName = crafterItem?.crafterName || crafterItem?.crafter?.name || 'Artigiano';
                          
                          return (
                            <div 
                              key={idx}
                              className="p-4 border-2 border-slate-200 rounded-lg bg-white"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-medium text-sm text-slate-700">{crafterName}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">{shipping.rule_name}</p>
                                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                                    <Clock className="w-3 h-3" />
                                    {shipping.delivery_days}
                                  </p>
                                </div>
                                <div className="text-right">
                                  {shipping.free_shipping_applied || shipping.shipping_cost === 0 ? (
                                    <div>
                                      <span className="font-semibold text-green-600">Gratis</span>
                                      <p className="text-xs text-green-600">
                                        {shipping.is_custom_order ? 'Offerta dall\'artigiano' : 'Soglia raggiunta!'}
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="font-semibold">€{Number(shipping.shipping_cost).toFixed(2)}</span>
                                  )}
                                </div>
                              </div>
                              {shipping.free_threshold && !shipping.free_shipping_applied && (
                                <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                  <Tag className="w-3 h-3" />
                                  Spedizione gratuita sopra €{shipping.free_threshold}
                                </p>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Total shipping */}
                        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                          <span className="font-medium">Totale spedizione</span>
                          <span className={`font-bold ${shippingCost === 0 ? 'text-green-600' : 'text-shop-primary'}`} data-testid="shipping-summary-total">
                            {shippingCost === 0 ? 'Gratis' : `€${shippingCost.toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 border-2 border-slate-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Spedizione Standard Italia</p>
                            <p className="text-sm text-slate-500">1-3 giorni lavorativi</p>
                          </div>
                          <span className={`font-semibold ${shippingCost === 0 ? 'text-green-600' : ''}`}>
                            {shippingCost === 0 ? 'Gratis' : `€${shippingCost.toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Ogni artigiano gestisce la propria spedizione
                    </p>
                  </div>
                  
                  <Button type="submit" className="w-full bg-shop-primary text-white py-6 mt-6" data-testid="continue-to-payment-btn">
                    Continua al pagamento
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </Card>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <Card className="p-6" data-testid="payment-section">
                <div className="flex items-center gap-2 mb-6">
                  {!checkingPayment && (
                    <button onClick={() => setStep(2)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-shipping">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                  )}
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-shop-primary" />
                    Pagamento
                  </h2>
                </div>

                {checkingPayment ? (
                  /* Verifying payment status */
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 animate-spin text-shop-primary mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Verifica pagamento in corso...</h3>
                    <p className="text-slate-500">Attendere prego, stiamo verificando il tuo pagamento.</p>
                  </div>
                ) : checkoutMode === 'express' ? (
                  /* --- Nuovo flow: SetupIntent + multi-charge (single payment UX) --- */
                  <div data-testid="express-checkout-container" className="space-y-4">
                    {/* Codice Sconto (anche nel flusso express) */}
                    <div className="border border-slate-200 rounded-lg p-4" data-testid="express-discount-section">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-5 h-5 text-shop-primary" />
                        <h4 className="font-medium">Codice Sconto</h4>
                      </div>
                      {discountApplied && discountApplied.valid ? (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3" data-testid="express-discount-applied">
                          <div className="flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-600" />
                            <div>
                              <p className="font-medium text-green-800">{discountCode}</p>
                              <p className="text-sm text-green-600">
                                -{discountApplied.discount_percentage}%
                                {discountApplied.product_title && ` su "${discountApplied.product_title}"`}
                              </p>
                            </div>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={removeDiscount} className="text-green-700 hover:text-green-800 hover:bg-green-100" data-testid="express-remove-discount-btn">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            placeholder="Inserisci codice"
                            className="flex-1 uppercase"
                            disabled={validatingDiscount}
                            data-testid="express-discount-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={validateDiscountCode}
                            disabled={validatingDiscount || !discountCode.trim()}
                            className="border-shop-primary text-shop-primary hover:bg-shop-primary/10"
                            data-testid="express-apply-discount-btn"
                          >
                            {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Applica'}
                          </Button>
                        </div>
                      )}
                    </div>
                    <SingleStripePayment
                      stripePromise={stripePromiseState}
                      cartItems={cartItems}
                      buyer={{ id: user?.id, email: user?.email || shippingAddress?.email }}
                      shippingAddress={shippingAddress}
                      shippingBreakdown={{ breakdown: shippingRows, total_shipping: shippingCost }}
                      shippingCost={shippingCost}
                      discountCode={discountApplied?.valid ? discountCode : null}
                      discountAmount={discountAmount}
                      shippingMethod={shippingMethod}
                      onAllSucceeded={handleMultiChargeResults}
                      onPartialSuccess={handleMultiChargeResults}
                    />
                  </div>
                ) : (
                  /* Stripe payment form */
                  <form onSubmit={handlePaymentSubmit} className="space-y-6">
                    {/* Stripe Info */}
                    <div className="bg-gradient-to-br from-shop-primary/10 to-shop-primary-dark/5 border border-shop-primary/20 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <CreditCard className="w-6 h-6 text-shop-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Pagamento sicuro con Stripe</h3>
                          <p className="text-sm text-slate-500">I tuoi dati sono protetti</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">
                        Verrai reindirizzato alla pagina di pagamento sicura di Stripe dove potrai inserire i dati della tua carta.
                        Accettiamo Visa, Mastercard, American Express e altre carte principali.
                      </p>
                    </div>

                    {/* Test Mode Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium">Modalità Test</p>
                          <p>Usa la carta di test: <code className="bg-amber-100 px-1 rounded">4242 4242 4242 4242</code> con qualsiasi data futura e CVC.</p>
                        </div>
                      </div>
                    </div>

                    {/* Discount Code Section */}
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-5 h-5 text-shop-primary" />
                        <h4 className="font-medium">Codice Sconto</h4>
                      </div>
                      
                      {discountApplied && discountApplied.valid ? (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-600" />
                            <div>
                              <p className="font-medium text-green-800">{discountCode}</p>
                              <p className="text-sm text-green-600">
                                -{discountApplied.discount_percentage}%
                                {discountApplied.product_title && ` su "${discountApplied.product_title}"`}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removeDiscount}
                            className="text-green-700 hover:text-green-800 hover:bg-green-100"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            placeholder="Inserisci codice"
                            className="flex-1 uppercase"
                            disabled={validatingDiscount}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={validateDiscountCode}
                            disabled={validatingDiscount || !discountCode.trim()}
                            className="border-shop-primary text-shop-primary hover:bg-shop-primary/10"
                          >
                            {validatingDiscount ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Applica'
                            )}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Order recap */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-600">Subtotale</span>
                        <span>€{subtotal.toFixed(2)}</span>
                      </div>
                      {discountAmount > 0 && (
                        <div className="flex justify-between items-center mb-2 text-green-600">
                          <span className="flex items-center gap-1">
                            <Tag className="w-4 h-4" />
                            Sconto ({discountApplied?.discount_percentage}%)
                          </span>
                          <span>-€{discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-600 flex items-center gap-1">
                          <Truck className="w-3 h-3" />
                          Spedizione
                        </span>
                        <span>
                          {loadingShipping ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : shippingCost === 0 ? (
                            <span className="text-green-600">Gratis</span>
                          ) : (
                            `€${shippingCost.toFixed(2)}`
                          )}
                        </span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between items-center font-bold text-lg">
                        <span>Totale</span>
                        <span className="text-shop-primary">€{total.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Security badges */}
                    <div className="flex items-center justify-center gap-6 py-2">
                      <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                        <Lock className="w-4 h-4" />
                        SSL 256-bit
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                        <Shield className="w-4 h-4" />
                        PCI Compliant
                      </div>
                    </div>
                    
                    {/* Withdrawal Rights Warning & Checkbox for Customized Products */}
                    {hasCustomizedProducts && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="checkout-withdrawal-warning">
                        <div className="flex items-start gap-3 mb-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-semibold text-amber-800 mb-1">Prodotti su misura nel carrello</p>
                            <p className="text-amber-700">
                              Uno o più articoli sono personalizzati. Per questi prodotti il <strong>diritto di recesso è escluso</strong> ai 
                              sensi dell'art. 59, comma 1, lett. c) del Codice del Consumo, salvo prodotto difettoso, 
                              non conforme alla descrizione o diverso da quanto promesso.
                            </p>
                          </div>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-300 transition-colors">
                          <input
                            type="checkbox"
                            checked={withdrawalAccepted}
                            onChange={(e) => setWithdrawalAccepted(e.target.checked)}
                            className="mt-1 w-4 h-4 text-shop-primary border-slate-300 rounded focus:ring-shop-primary"
                            data-testid="withdrawal-checkbox"
                          />
                          <span className="text-sm text-slate-700">
                            <strong>Dichiaro di aver letto e compreso</strong> che per i prodotti personalizzati/su misura 
                            non potrò esercitare il diritto di recesso, salvo difetto o non conformità.
                          </span>
                        </label>
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-shop-primary hover:bg-shop-primary-dark text-white py-6 text-lg"
                      disabled={loading || (hasCustomizedProducts && !withdrawalAccepted)}
                      data-testid="pay-now-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Reindirizzamento a Stripe...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          Paga €{total.toFixed(2)} con Stripe
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-center text-slate-400">
                      Cliccando "Paga" accetti i nostri Termini di Servizio e la Privacy Policy
                    </p>
                  </form>
                )}
              </Card>
            )}
          </div>

          {/* Order Summary Sidebar */}
          {step !== 4 && (
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-32" data-testid="order-summary">
                <h3 className="font-bold text-lg mb-4">Riepilogo ordine</h3>
                
                {/* Items summary */}
                <div className="space-y-3 mb-4">
                  {cartItems.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                        {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        <p className="text-xs text-slate-500">x{item.quantity}</p>
                      </div>
                      <span className="text-sm font-semibold">€{(item.totalPrice * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {cartItems.length > 3 && (
                    <p className="text-sm text-slate-500">+ altri {cartItems.length - 3} articoli</p>
                  )}
                </div>
                
                <Separator className="my-4" />
                
                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotale</span>
                    <span>€{subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-green-600" data-testid="summary-discount-row">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Sconto ({discountApplied?.discount_percentage}%)
                      </span>
                      <span>-€{discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {(() => {
                    const { customOrdersShipping, regularShipping } = getShippingBreakdownInfo();
                    const hasCustomShipping = customOrdersShipping > 0;
                    const hasRegularShipping = regularShipping > 0 || cartItems.some(item => !item.isCustomOrder);
                    
                    return (
                      <>
                        {hasCustomShipping && (
                          <div className="flex justify-between">
                            <span className="text-slate-600 flex items-center gap-1">
                              <Truck className="w-3 h-3" />
                              Sped. ordini su misura
                            </span>
                            <span className={customOrdersShipping === 0 ? 'text-green-600 font-medium' : ''}>
                              {customOrdersShipping === 0 ? 'Gratis' : `€${customOrdersShipping.toFixed(2)}`}
                            </span>
                          </div>
                        )}
                        {hasRegularShipping && !hasCustomShipping && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Spedizione</span>
                            <span className={shippingCost === 0 ? 'text-green-600 font-medium' : ''}>
                              {shippingCost === 0 ? 'Gratis' : `€${shippingCost.toFixed(2)}`}
                            </span>
                          </div>
                        )}
                        {hasRegularShipping && hasCustomShipping && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Sped. prodotti</span>
                            <span className={regularShipping === 0 ? 'text-green-600 font-medium' : ''}>
                              {regularShipping === 0 ? 'Gratis' : `€${regularShipping.toFixed(2)}`}
                            </span>
                          </div>
                        )}
                        {!hasCustomShipping && !hasRegularShipping && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Spedizione</span>
                            <span className="text-green-600 font-medium">Gratis</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {shippingCost > 0 && subtotal < 100 && !cartItems.some(item => item.isCustomOrder) && (
                    <p className="text-xs text-amber-600">
                      Aggiungi €{(100 - subtotal).toFixed(2)} per la spedizione gratuita
                    </p>
                  )}
                </div>
                
                <Separator className="my-4" />
                
                <div className="flex justify-between items-center font-bold text-lg">
                  <span>Totale</span>
                  <span className="text-shop-primary">€{total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">IVA inclusa</p>
                
                {step === 1 && (
                  <>
                    {hasBlockedItems && (
                      <div
                        className="mt-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm"
                        data-testid="cart-blocked-crafters-warning"
                      >
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-amber-900">
                              {blockedItems.length === 1
                                ? '1 prodotto non è acquistabile'
                                : `${blockedItems.length} prodotti non sono acquistabili`}
                            </p>
                            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                              L&apos;artigiano sta completando l&apos;attivazione dei pagamenti.
                              Rimuovi questi articoli per procedere:
                            </p>
                            <ul className="mt-2 space-y-0.5 text-xs text-amber-900">
                              {blockedItems.map((it, idx) => (
                                <li key={idx} className="flex items-center gap-1">
                                  <span>•</span>
                                  <span className="font-medium truncate">{it.title || it.name || 'Prodotto'}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    <Button 
                      onClick={handleCheckout}
                      className="w-full bg-shop-primary text-white mt-6 py-6 disabled:opacity-60"
                      disabled={hasBlockedItems}
                      data-testid="proceed-to-checkout-btn"
                    >
                      {hasBlockedItems ? 'Rimuovi i prodotti bloccati' : 'Procedi al checkout'}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}

                {/* Trust badges */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Shield className="w-4 h-4 text-green-600" />
                    Pagamento sicuro
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Truck className="w-4 h-4 text-shop-primary" />
                    Spedizione tracciata
                  </div>
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <Package className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-col">
                      <span>Reso entro 14 giorni</span>
                      <span className="text-[11px] text-slate-400 leading-tight">
                        Escluso per prodotti personalizzati o su misura
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
